import { Type } from '@angular/core';
import { Params } from '@angular/router';
import { MotifTableHeaderRendererComponent } from '@ey-xd/ng-motif';
import {
  ConnectorStatus,
  DataResponse,
  FilterDropdownOption,
  FilterGroup,
  GridData,
  IDataConnector,
  JSONObject,
  JSONValue,
  PageSizeOptions,
  RowDataDefinition,
  sortDirectionType,
} from '@models';
import { FieldType, FilterCriterion } from '@models/filter.model';
import { getFormattedEnumKeyByValue } from '@utils';
import { AgRendererComponent } from 'ag-grid-angular';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { BehaviorSubject, catchError, EMPTY, filter, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { convertDateTimeToDateOnlyString } from 'utils/date-utils';

export class DataConnector<T> implements IDataConnector {
  #sortBy: string | null = null;
  #sortDirection: sortDirectionType | null = null;
  #page = 1;
  #pageSize = PageSizeOptions[0].value;
  #searchTerm = '';
  #latestRequestId = 0;

  #filterGroup: FilterGroup[] = [];

  #data$: BehaviorSubject<GridData> = new BehaviorSubject<GridData>({
    rows: [],
    totalPages: 0,
    rowCount: 0,
  });
  #status$ = new BehaviorSubject<ConnectorStatus>({ isLoading: true });

  #dataParams$ = new BehaviorSubject<Params>({});

  public static DEFAULT_PAGE_SIZE = 10;
  public static DEFAULT_PAGE_NUMBER = 1;

  constructor(
    private defaultParams: Params,
    private getData: (params: Params) => Observable<DataResponse<T>>,
    private map: (x: T) => RowDataDefinition,
    private filterCriteria: FilterCriterion[]
  ) {
    this.#sortBy = this.defaultParams['sortExpression']?.split(' ')[0] || null;
    this.#sortDirection = (this.defaultParams['sortExpression']?.split(' ')[1] as sortDirectionType) || null;
    this.#pageSize = this.defaultParams['pageSize'] || DataConnector.DEFAULT_PAGE_SIZE;
  }

  /**
   * Builds request parameters by merging defaults with provided params.
   * @param params Optional query parameters.
   * @returns Merged request parameters.
   */
  static buildRequestParams(params?: Params): Params {
    const result: Params = {
      pageSize: params?.['pageSize'] || this.DEFAULT_PAGE_SIZE,
      pageNumber: params?.['pageNumber'] || this.DEFAULT_PAGE_NUMBER,
      sortExpression: params?.['sortExpression'],
    };

    if (params?.['filterGroups'] && Array.isArray(params['filterGroups']) && params['filterGroups'].length > 0) {
      result['filterGroups'] = params['filterGroups'];
    }

    if (params?.['search'] && typeof params['search'] === 'string' && params['search'].trim() !== '') {
      result['search'] = params['search'];
    }

    return result;
  }

  /**
   * Creates a column definition for the grid.
   *
   * Supports both object-based and positional arguments for backward compatibility.
   *
   * @param fieldOrParams - Either:
   *   - An object containing column properties:
   *     - `field` (string): The field name in the data row.
   *     - `headerName` (string): The display name for the column header.
   *     - `cellRenderer` (optional): Cell renderer component or function.
   *     - `cellRendererParams` (optional): Parameters for the cell renderer.
   *   - Or, a string field name (deprecated, use object form).
   * @param headerName - The display name for the column header (positional, deprecated).
   * @param cellRenderer - Cell renderer component or function (positional, deprecated).
   * @param cellRendererParams - Parameters for the cell renderer (positional, deprecated).
   * @returns {ColDef} The column definition object for ag-Grid.
   *
   * @example
   * // Recommended: Object input
   * const col = DataConnector.createGridColumn({
   *   field: 'requestId',
   *   headerName: 'Request ID',
   *   cellRenderer: RequestIdColumnComponent
   * });
   *
   * @example
   * // With cellRendererParams
   * const col = DataConnector.createGridColumn({
   *   field: 'type',
   *   headerName: 'Type',
   *   cellRenderer: TypeColumnComponent,
   *   cellRendererParams: { showType: true }
   * });
   *
   * @example
   * // Legacy: Positional arguments
   * const col = DataConnector.createGridColumn('requestId', 'Request ID', RequestIdColumnComponent);
   */
  static createGridColumn = (
    fieldOrParams:
      | string
      | {
          field: string;
          headerName: string;
          cellRenderer?: Type<AgRendererComponent> | ((params: ICellRendererParams) => string | number | void);
          cellRendererParams?: object;
          sortable?: boolean;
          headerClass?: string; // optional header class
          minWidth?: number;
          maxWidth?: number;
          width?: number;
        },
    headerName?: string,
    cellRenderer?: Type<AgRendererComponent> | ((params: ICellRendererParams) => string | number | void),
    cellRendererParams?: object
  ): ColDef => {
    if (typeof fieldOrParams === 'object') {
      const { field, headerName, cellRenderer, cellRendererParams, sortable, headerClass, minWidth, maxWidth, width } =
        fieldOrParams;
      return {
        field,
        headerComponent: MotifTableHeaderRendererComponent,
        headerName,
        comparator: () => 0, // disable client-side sorting behavior
        ...(cellRenderer && { cellRenderer }),
        ...(cellRendererParams && { cellRendererParams }),
        ...(typeof sortable === 'boolean' && { sortable }),
        // add non-sortable header class to disable pointer events on label
        ...(sortable === false && { headerClass: (headerClass ? headerClass + ' ' : '') + 'non-sortable' }),
        ...(sortable !== false && headerClass && { headerClass }),
        ...(minWidth && { minWidth }),
        ...(maxWidth && { maxWidth }),
        ...(width && { width }),
      };
    } else {
      return {
        field: fieldOrParams,
        headerComponent: MotifTableHeaderRendererComponent,
        headerName: headerName!,
        comparator: () => 0, // disable client-side sorting behavior
        ...(cellRenderer && { cellRenderer }),
        ...(cellRendererParams && { cellRendererParams }),
      };
    }
  };

  public getRows$(): Observable<GridData> {
    return this.#data$.asObservable();
  }

  public getStatus$(): Observable<ConnectorStatus> {
    return this.#status$.asObservable();
  }

  public getDataParams$(): Observable<Params> {
    return this.#dataParams$.asObservable();
  }

  public onFilter(newFilters: FilterGroup[]): void {
    this.#filterGroup = structuredClone(newFilters) as FilterGroup[];
    this.#page = 1;
  }

  public onPageChange(page: number, pageSize: number): void {
    this.#page = page;
    this.#pageSize = pageSize;
    this.#refreshData();
  }

  public onSearch(searchTerm: string): void {
    this.setSearchTerm(searchTerm);
    this.#refreshData();
  }

  public onSearch$(searchTerm: string): Observable<GridData> {
    this.setSearchTerm(searchTerm);
    return this.#refreshData$();
  }

  public setSearchTerm(searchTerm: string): void {
    this.#searchTerm = searchTerm;
    this.#page = 1;
  }

  public onSort(columnName: string, sortDirection: sortDirectionType): void {
    this.#sortBy = columnName;
    this.#sortDirection = sortDirection;
    this.#page = 1;
    this.#refreshData();
  }

  public refresh(): void {
    this.refresh$().pipe(take(1)).subscribe();
  }

  public refresh$(): Observable<GridData> {
    return this.#refreshData$().pipe(
      tap((data: GridData): void => {
        this.#data$.next(data);
      })
    );
  }

  public setFilters(filters: FilterGroup[]): void {
    this.#page = 1;
    this.#filterGroup = [...filters];
  }

  public setPageNumber(page: number): void {
    this.#page = page;
  }

  public setStatus(status: ConnectorStatus): void {
    this.#status$.next(status);
  }

  #refreshData(): void {
    this.refresh();
  }

  #refreshData$(): Observable<GridData> {
    const requestId = ++this.#latestRequestId;
    this.#status$.next({ isLoading: true });
    const params: Params = this.#getDataParams();
    this.#dataParams$.next(params);
    return this.getData(params).pipe(
      switchMap((data: DataResponse<T>) => {
        const totalPages = Math.max(Math.ceil(data.totalCount / (this.#pageSize as number)), 1);
        if (this.#page > totalPages) {
          this.#page = totalPages;
          const correctedParams = this.#getDataParams();
          this.#dataParams$.next(correctedParams);
          return this.getData(correctedParams);
        }

        return of(data);
      }),
      filter(() => requestId === this.#latestRequestId),
      map((data: DataResponse<T>): GridData => {
        const totalPages = Math.max(Math.ceil(data.totalCount / (this.#pageSize as number)), 1);
        return {
          rowCount: data.totalCount,
          totalPages: totalPages,
          rows: data.items.map(this.map),
        } as GridData;
      }),
      tap(() => this.#status$.next({ isLoading: false })),
      catchError(() => {
        if (requestId === this.#latestRequestId) {
          this.#status$.next({ isLoading: false });
        }
        return EMPTY;
      })
    );
  }

  #getDataParams(): Params {
    return {
      pageSize: this.#pageSize,
      pageNumber: this.#page,
      sortExpression: this.#sortBy && this.#sortDirection ? `${this.#sortBy} ${this.#sortDirection}` : null,
      filterGroups: this.#mapFiltersBeforeSending(this.#filterGroup),
      search: this.#searchTerm,
    };
  }

  #mapFiltersBeforeSending(filters: FilterGroup[]): FilterGroup[] {
    const newFilters = structuredClone(filters) as FilterGroup[];
    newFilters.forEach(filter => {
      filter.entityPropertyFilters.forEach(entityPropertyFilter => {
        const criterion = this.filterCriteria.find(criteria => criteria.key === entityPropertyFilter.propertyName);
        if (criterion?.type === FieldType.Date) {
          entityPropertyFilter.propertyName = criterion.field;
          entityPropertyFilter.value = convertDateTimeToDateOnlyString(entityPropertyFilter.value as Date);
        }

        if (criterion?.type === FieldType.Number) {
          entityPropertyFilter.propertyName = criterion.field;
        }

        if (criterion?.type === FieldType.Lookup || criterion?.type === FieldType.MultipleLookup) {
          entityPropertyFilter.propertyName = criterion.field;
          if (Array.isArray(entityPropertyFilter.value)) {
            entityPropertyFilter.value = entityPropertyFilter.value.map(value => this.#getLookupValue(value));
          } else {
            entityPropertyFilter.value = this.#getLookupValue(entityPropertyFilter.value);
          }
        }

        delete entityPropertyFilter.field;
      });
    });

    return newFilters;
  }

  #getLookupValue(value: JSONValue): JSONValue {
    if (typeof value === 'object' && value !== null && !Array.isArray(value) && 'value' in value) {
      return value['value'];
    }

    return value;
  }

  /**
   * Generates filter options based on the provided enum-like object.
   * Each option includes a value, key, and display name derived from the object.
   *
   * @param enumObject An object representing an enum-like structure with numeric values.
   * @returns An array of filter options with value, key, and display name.
   */
  public static generateFilterOptionsFromEnum(enumObject: JSONObject): FilterDropdownOption[] {
    return Object.values(enumObject)
      .filter(value => typeof value === 'number')
      .map(
        (value, index): FilterDropdownOption =>
          ({
            id: index,
            value,
            key: value,
            displayName: getFormattedEnumKeyByValue(enumObject, value) as string,
          }) as unknown as FilterDropdownOption
      );
  }

  public get sortBy(): string | null {
    return this.#sortBy;
  }

  public get sortDirection(): sortDirectionType | null {
    return this.#sortDirection;
  }

  public get pageSize(): number | null {
    return this.#pageSize;
  }
}
